import { useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, Link } from "@mui/material";
import { brainTokens } from "../../../design/brainTheme";
import { PersonAvatar, SectionCard } from "../../../mui";
import {
  getProjectEvents,
  type Project,
  type ProjectEvent,
  type ProjectHeartbeatResponse,
  type ProjectJiraIssueRow,
} from "../../../lib/brain-api";
import { MetricStrip, type MetricCell } from "./MetricStrip";
import { StatusSpread } from "./StatusSpread";
import { RecordRail } from "./RecordRail";
import { ProjectHeartbeatPanel } from "../ProjectHeartbeatPanel";

const c = brainTokens.color;
const ISSUE_KEY_RE = /\b([A-Z][A-Z0-9]+-\d+)\b/;

function shortRel(iso: string | undefined): { value: string; unit: string } | null {
  if (!iso) return null;
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return { value: "0", unit: "m ago" };
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return { value: String(Math.max(1, mins)), unit: "m ago" };
  const hours = Math.floor(mins / 60);
  if (hours < 48) return { value: String(hours), unit: "h ago" };
  const days = Math.floor(hours / 24);
  return { value: String(days), unit: "d ago" };
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  const day = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day} ${time}`;
}

function splitEventText(e: ProjectEvent): { verb: string; object: string; mono: boolean } {
  if (e.objectKey) {
    const idx = e.text.lastIndexOf(e.objectKey);
    if (idx >= 0) {
      const verb = e.text.slice(0, idx).replace(/\s*[—\-]\s*$/, "").trim();
      return { verb: verb || e.text, object: e.objectKey, mono: true };
    }
  }
  const dash = e.text.match(/^(.*?)\s*[—\-]\s*(.+)$/);
  if (dash) {
    const object = dash[2].trim();
    return { verb: dash[1].trim(), object, mono: ISSUE_KEY_RE.test(object) };
  }
  return { verb: e.text, object: "", mono: false };
}

function firstName(actor: string): string {
  const t = actor.trim();
  if (!t) return "—";
  return t.split(/\s+/)[0] ?? t;
}

type PersonRow = { name: string; roles: string[]; keys: string[] };

export function buildPeople(
  owner: string | undefined,
  lead: string | undefined,
  deputy: string | undefined,
  boardIssues: ProjectJiraIssueRow[],
): PersonRow[] {
  const order: string[] = [];
  const map = new Map<string, PersonRow>();

  const add = (name: string | undefined | null, role: string) => {
    const n = (name ?? "").trim();
    if (!n) return;
    const k = n.toLowerCase();
    let row = map.get(k);
    if (!row) {
      row = { name: n, roles: [], keys: [] };
      map.set(k, row);
      order.push(k);
    }
    if (!row.roles.includes(role)) row.roles.push(role);
  };

  add(owner, "owner");
  add(lead, "lead");
  add(deputy, "deputy");

  for (const i of boardIssues) {
    if (!i.assignee) continue;
    // Board scopes keep closed work for BOARD_SCOPE_RECENT_DAYS so the card can show
    // "done this week". The roster is who is ON something — a Done/Released key is not
    // a person's workload, so it must not put them on the roster or in their key list.
    if (String(i.statusCategory).toLowerCase() === "done") continue;
    add(i.assignee, "working");
    const k = i.assignee.trim().toLowerCase();
    const row = map.get(k);
    if (row && !row.keys.includes(i.key)) row.keys.push(i.key);
  }

  return order.map((k) => {
    const row = map.get(k)!;
    const roles = row.roles.filter((r) => r !== "working");
    return {
      ...row,
      roles: roles.length > 0 ? roles : row.roles.includes("working") ? ["on issues"] : row.roles,
    };
  });
}

function scopeBoardCount(project: Project | null): number {
  if (project?.jiraScopes?.length) return project.jiraScopes.length;
  if (project?.jiraScope) return 1;
  return 0;
}

export function ProjectOverviewTab({
  projectId,
  project,
  draft,
  editing,
  canEdit,
  busy,
  heartbeat,
  boardIssues,
  customerId,
  customerName,
  owner,
  jiraKey,
  fileCount,
  atomCount,
  onGoBoard,
  onGoActivity,
  onEdit,
  onCancel,
  onSave,
  onDelete,
  setDraft,
  onScopesChanged,
}: {
  projectId: string;
  project: Project | null;
  draft: Project | null;
  editing: boolean;
  canEdit: boolean;
  busy: boolean;
  heartbeat: ProjectHeartbeatResponse | null;
  boardIssues: ProjectJiraIssueRow[];
  customerId?: string;
  customerName?: string;
  owner?: string;
  jiraKey?: string;
  fileCount: number;
  atomCount: number;
  onGoBoard: () => void;
  onGoActivity: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  setDraft: (patch: Partial<Project>) => void;
  onScopesChanged: () => void;
}) {
  const [events, setEvents] = useState<ProjectEvent[]>([]);

  useEffect(() => {
    let cancelled = false;
    getProjectEvents(projectId)
      .then((r) => {
        if (!cancelled) setEvents(r.events ?? []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const rollups = heartbeat?.rollups;
  const progress = heartbeat?.progress;
  const weeklyTrend = heartbeat?.weeklyTrend;
  const empty =
    (rollups?.total ?? boardIssues.length) === 0 && scopeBoardCount(project) === 0;

  const openN = rollups?.open ?? Math.max(0, (rollups?.total ?? 0) - (rollups?.done ?? 0));
  const doneN = progress?.done ?? rollups?.done ?? 0;
  const inFlight = progress?.inProgress ?? 0;
  /** Issue blockers from heartbeat rollups — not the project-level blocked flag. */
  const blockedN = rollups?.blocked ?? 0;

  const recent = events.slice(0, 5);
  const last = events[0];
  const lastRel = shortRel(last?.at);

  const weekStart = useMemo(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? 6 : day - 1;
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - diff);
    return d.getTime();
  }, []);

  const standupMentionsThisWeek = useMemo(() => {
    const fromEvents = events.filter(
      (e) => e.type === "standup" && Date.parse(e.at) >= weekStart,
    ).length;
    if (fromEvents > 0) return fromEvents;
    return (heartbeat?.mentionEvents ?? []).filter((m) => {
      const t = m.createdAt || Date.parse(`${m.nzDate}T12:00:00`);
      return t >= weekStart;
    }).length;
  }, [events, heartbeat?.mentionEvents, weekStart]);

  const blockerTile = blockedN > 0
    ? { tileBg: c.dangerBg, tileColor: c.dangerText }
    : { tileBg: c.successBg, tileColor: c.successText };

  const metrics: MetricCell[] = empty
    ? [
        { label: "OPEN WORK", value: "0", unit: "issues", sub: "no scope linked", icon: "clipboard-list", tileBg: c.tealTint, tileColor: c.tealInk },
        { label: "THIS WEEK", value: "0", unit: "done", sub: "nothing yet", valueColor: c.placeholder, icon: "check", tileBg: c.successBg, tileColor: c.successText },
        {
          label: "BLOCKERS",
          value: String(blockedN),
          unit: "",
          sub: blockedN > 0 ? "flagged on board" : "clear",
          valueColor: blockedN > 0 ? c.dangerText : c.ink,
          icon: "shield-check",
          ...blockerTile,
        },
        { label: "LAST ACTIVITY", value: "0", unit: "", sub: "nothing yet", valueColor: c.placeholder, icon: "clock", tileBg: c.panelBg2, tileColor: c.muted },
      ]
    : [
        {
          label: "OPEN WORK",
          value: String(openN),
          unit: "issues",
          sub: `${doneN} done · ${inFlight} in flight`,
          icon: "clipboard-list",
          tileBg: c.tealTint,
          tileColor: c.tealInk,
        },
        {
          label: "THIS WEEK",
          value: weeklyTrend
            ? `${weeklyTrend.delta >= 0 ? "+" : ""}${weeklyTrend.delta}`
            : "0",
          unit: "done",
          sub: weeklyTrend
            ? `${weeklyTrend.doneThisWeek} closed · ${weeklyTrend.donePrevWeek} prior week`
            : "nothing yet",
          valueColor: weeklyTrend
            ? weeklyTrend.delta >= 0
              ? c.successText
              : c.dangerText
            : c.placeholder,
          icon: "check",
          tileBg: c.successBg,
          tileColor: c.successText,
        },
        {
          label: "BLOCKERS",
          value: String(blockedN),
          unit: "",
          sub: blockedN > 0 ? "flagged on board" : "clear",
          valueColor: blockedN > 0 ? c.dangerText : c.ink,
          icon: "shield-check",
          ...blockerTile,
        },
        {
          label: "LAST ACTIVITY",
          value: lastRel?.value ?? "0",
          unit: lastRel?.unit ?? "",
          sub: last
            ? `${firstName(last.actor)} · ${standupMentionsThisWeek} standup mention${standupMentionsThisWeek === 1 ? "" : "s"}`
            : "nothing yet",
          valueColor: lastRel ? c.ink : c.placeholder,
          icon: "clock",
          tileBg: c.panelBg2,
          tileColor: c.muted,
        },
      ];

  const people = buildPeople(owner, project?.assignee, project?.deputy, boardIssues);

  const lastActivityLine = lastRel && last
    ? `Last activity ${lastRel.value}${lastRel.unit.replace(" ago", "")} ago · ${firstName(last.actor)}`
    : "Last activity —";

  return (
    <Box sx={{ display: "flex", gap: "22px", alignItems: "flex-start", pt: "20px" }}>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
        <MetricStrip cells={metrics} />
        <StatusSpread
          issues={boardIssues}
          boardCount={scopeBoardCount(project)}
          onOpenBoard={onGoBoard}
        />

        <SectionCard label="People" padding={0} headerMb={0}>
          <Box sx={{ px: "22px", pt: "20px", pb: "6px" }}>
            <Box
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 10,
                letterSpacing: "0.12em",
                color: c.faint,
              }}
            >
              PEOPLE
            </Box>
          </Box>
          <Box sx={{ px: "22px", pb: "18px" }}>
            {people.length === 0 ? (
              <Box sx={{ py: "10px", fontSize: 13, color: c.muted2 }}>No one assigned yet.</Box>
            ) : (
              people.map((p) => (
                <Box
                  key={p.name}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "11px",
                    py: "10px",
                    borderBottom: `1px solid ${c.borderLight3}`,
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <PersonAvatar name={p.name} size={28} />
                  <Box component="span" sx={{ fontSize: 14, color: c.ink2, fontWeight: 500 }}>
                    {p.name}
                  </Box>
                  <Box component="span" sx={{ fontSize: 12.5, color: c.muted }}>
                    {p.roles.join(" · ")}
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  {p.keys.length > 0 && (
                    <Box
                      component="span"
                      sx={{
                        fontFamily: brainTokens.font.mono,
                        fontSize: 11,
                        color: c.citeText,
                        background: c.citeBg,
                        border: `1px solid ${c.citeBorder}`,
                        borderRadius: "5px",
                        padding: "2px 7px",
                      }}
                    >
                      {p.keys.join(" · ")}
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>
        </SectionCard>

        <SectionCard label="Recent" padding={0} headerMb={0}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", px: "22px", pt: "20px", pb: "6px" }}>
            <Box
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 10,
                letterSpacing: "0.12em",
                color: c.faint,
              }}
            >
              RECENT
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box
              component={ButtonBase}
              onClick={onGoActivity}
              sx={{
                fontSize: 13,
                color: c.tealInk,
                fontWeight: 500,
                fontFamily: "inherit",
                lineHeight: 1.2,
              }}
            >
              Full activity →
            </Box>
          </Box>
          <Box sx={{ px: "22px", pb: recent.length ? "6px" : "18px" }}>
            {recent.length === 0 ? null : (
              recent.map((e, i) => {
                const { verb, object, mono } = splitEventText(e);
                return (
                  <Box
                    key={`${e.at}-${e.actor}-${i}`}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "12px",
                      py: "9px",
                      borderBottom: `1px solid ${c.borderLight3}`,
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: brainTokens.font.mono,
                        fontSize: 11.5,
                        color: c.muted2,
                        flex: "none",
                        width: 84,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatWhen(e.at)}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 13,
                        color: e.actorKind === "agent" ? c.tealInk : c.ink2,
                        flex: "none",
                        width: 104,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {e.actor}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        color: c.muted,
                        lineHeight: 1.5,
                      }}
                    >
                      {verb}
                      {object ? " " : ""}
                      {object ? (
                        e.objectUrl ? (
                          <Link
                            href={e.objectUrl}
                            target="_blank"
                            rel="noopener"
                            underline="hover"
                            sx={{
                              fontFamily: mono ? brainTokens.font.mono : "inherit",
                              color: c.tealInk,
                              fontSize: 13,
                            }}
                          >
                            {object}
                          </Link>
                        ) : (
                          <Box
                            component="span"
                            sx={{
                              fontFamily: mono ? brainTokens.font.mono : "inherit",
                              color: c.ink3,
                            }}
                          >
                            {object}
                          </Box>
                        )
                      ) : null}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </SectionCard>
      </Box>

      <Box sx={{ width: 340, flex: "none", display: "flex", flexDirection: "column", gap: "14px" }}>
        <RecordRail
          project={project}
          draft={draft}
          editing={editing}
          canEdit={canEdit}
          busy={busy}
          customerId={customerId}
          customerName={customerName}
          owner={owner}
          jiraKey={jiraKey}
          fileCount={fileCount}
          atomCount={atomCount}
          lastActivityLine={lastActivityLine}
          standupMentionsThisWeek={standupMentionsThisWeek}
          onEdit={onEdit}
          onCancel={onCancel}
          onSave={onSave}
          onDelete={onDelete}
          setDraft={setDraft}
          onScopesChanged={onScopesChanged}
        />
        <ProjectHeartbeatPanel heartbeat={heartbeat} />
      </Box>
    </Box>
  );
}
